import { useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { ScrollToTop } from '@/components/ui/scroll-to-top';
import { HeaderThemeToggle } from '@/components/layout/header/HeaderThemeToggle';

export function SectionForbiddenScreen() {
  const { t, user } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Minimal header bar with theme toggle */}
      <div className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <a
            href={`/${currentLang}`}
            className="flex items-center gap-2 text-muted-foreground/70 transition-colors hover:text-foreground"
          >
            <Icon name="arrowLeft" className="h-4 w-4" />
            <span className="text-sm font-medium">{t.backToHome}</span>
          </a>

          <HeaderThemeToggle />
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="relative py-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,420px)] lg:items-end">
            <div>
              <p className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-destructive/70">
                {t.accessDeniedTitle}
              </p>
              <h1 className="mt-4 font-semibold tracking-tight text-foreground text-3xl sm:text-4xl">
                403
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-muted-foreground/80 sm:text-base">
                {t.accessDeniedDesc}
              </p>
            </div>

            {/* Signal cards */}
            <div className="grid grid-cols-2 gap-0 overflow-hidden rounded-2xl border border-border/40 bg-background/60">
              <Signal label={t.username} value={user?.username || '—'} />
              <Signal label={t.accessDeniedYourRole} value={user?.role || '—'} />
            </div>
          </div>
        </header>

        {/* Info section */}
        <section className="py-7">
          <div className="max-w-md">
            <div className="flex items-start gap-3 rounded-2xl border border-border/40 bg-background/60 px-5 py-4">
              <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-destructive/60" />
              <p className="text-sm leading-7 text-muted-foreground/80">
                {t.accessDeniedGeneric}
              </p>
            </div>
          </div>
        </section>

        {/* Actions */}
        <section className="py-7">
          <div className="flex max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:flex-wrap">
            <a href={`/${currentLang}`}>
              <Button className="h-11 w-full rounded-full sm:w-auto border border-primary/20 bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-colors hover:bg-primary/90">
                <Icon name="home" className="mr-2 h-4 w-4" />
                {t.backToHomeCta}
              </Button>
            </a>
            <a href={`/${currentLang}/settings`}>
              <Button
                variant="outline"
                className="h-11 w-full rounded-full sm:w-auto border-border/40 bg-background/60 px-5 text-sm font-semibold text-foreground shadow-none transition-colors hover:bg-muted/30"
              >
                <Icon name="mail" className="mr-2 h-4 w-4" />
                {t.accessDeniedContactAdmin}
              </Button>
            </a>
          </div>
        </section>
      </div>

      <ScrollToTop />
    </div>
  );
}

function Signal({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 border-r border-border/40 px-4 py-3 last:border-r-0">
      <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">
        {label}
      </span>
      <span className="mt-1 block truncate text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}
